import React, { useEffect, useState } from 'react';
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Compass,
  ExternalLink,
  Images,
  LayoutDashboard,
  LogOut,
  MapPin,
  Mail,
  Menu,
  Newspaper,
  RefreshCw,
  Settings,
  Ticket,
  UserRound,
  Users,
  X,
} from 'lucide-react';
import { getAuthSession, hasAuthSession } from '../lib/api';
import { logoutUser } from '../lib/portalApi';
import BrandLogo from './BrandLogo';

// WordPress-style admin frame: collapsible sidebar + sticky top bar with global
// actions. Add a section by dropping an entry into NAV and a <Route> in App.js.
const NAV = [
  { to: '/admin/dashboard', label: 'Panel', icon: LayoutDashboard, desc: 'Estadísticas' },
  { to: '/admin/activities', label: 'Actividades', icon: Compass, desc: 'Catálogo y contenido' },
  { to: '/admin/reservations', label: 'Reservas', icon: CalendarDays, desc: 'Reservas y solicitudes' },
  { to: '/admin/destinations', label: 'Destinos', icon: MapPin, desc: 'Islas y zonas' },
  { to: '/admin/blog', label: 'Blog', icon: Newspaper, desc: 'Entradas y artículos' },
  { to: '/admin/customers', label: 'Clientes', icon: Users, desc: 'Usuarios registrados' },
  { to: '/admin/coupons', label: 'Cupones', icon: Ticket, desc: 'Descuentos' },
  { to: '/admin/newsletter', label: 'Newsletter', icon: Mail, desc: 'Suscriptores y campañas' },
  { to: '/admin/media', label: 'Medios', icon: Images, desc: 'Imágenes y galerías' },
  { to: '/admin/settings', label: 'Ajustes', icon: Settings, desc: 'Pagos y módulos' },
  { to: '/admin/account', label: 'Mi cuenta', icon: UserRound, desc: 'Perfil y contraseña' },
];

const COLLAPSE_KEY = 'owa-admin-sidebar-collapsed';

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const session = getAuthSession();

  const [drawer, setDrawer] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => (typeof window !== 'undefined' ? window.localStorage.getItem(COLLAPSE_KEY) === '1' : false)
  );
  const [purge, setPurge] = useState('idle'); // idle | working | done

  useEffect(() => {
    window.localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => { setDrawer(false); }, [location.pathname]);

  // Client-side guard (the API also enforces admin on every write).
  if (!hasAuthSession()) return <Navigate to="/login" replace />;
  if (session.user?.role !== 'admin') return <Navigate to="/account" replace />;

  const current = NAV.find((item) => location.pathname.startsWith(item.to));

  const handleLogout = () => {
    logoutUser();
    navigate('/login');
  };

  // Drop cached content so freshly edited data is re-fetched, and clear any
  // browser-held asset caches before reloading.
  const handlePurge = async () => {
    setPurge('working');
    queryClient.clear();
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      if (navigator.serviceWorker?.getRegistrations) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } catch {
      /* cache APIs are best-effort; the reload below still refreshes data */
    }
    setPurge('done');
    setTimeout(() => window.location.reload(), 400);
  };

  const linkClass = ({ isActive }) =>
    `group flex items-center gap-3 rounded-2xl px-3 py-3 text-sm transition ${
      isActive
        // Navy on the brand teal reads at 5.3:1; white on that teal is only 3:1.
        ? 'bg-[#1fa5a3] text-[#0b1830] shadow-[0_12px_30px_-14px_rgba(31,165,163,0.9)]'
        : 'text-white/70 hover:bg-white/10 hover:text-white'
    }`;

  const Sidebar = ({ mini = false }) => (
    <div className="flex h-full min-h-0 flex-col gap-5 p-4">
      <div className={`flex-shrink-0 ${mini ? 'flex justify-center px-0' : 'px-1'}`}>
        {mini ? (
          <NavLink to="/admin/activities" className="block h-10 w-10 overflow-hidden rounded-full">
            <img src="/owa-coin.webp" alt="OWA" className="h-full w-full object-contain" />
          </NavLink>
        ) : (
          <>
            <BrandLogo variant="footer" to="/admin/activities" />
            <p className="mt-3 text-[10px] font-semibold uppercase tracking-[0.24em] text-[#1fa5a3]">
              Panel de administración
            </p>
          </>
        )}
      </div>

      <nav className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto [scrollbar-width:thin]">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={linkClass}
            title={mini ? item.label : undefined}
            onClick={() => setDrawer(false)}
          >
            {({ isActive }) => (
              <>
                <item.icon className={`h-5 w-5 flex-shrink-0 ${isActive ? 'text-[#0b1830]' : 'text-[#1fa5a3]'} ${mini ? 'mx-auto' : ''}`} />
                {!mini ? (
                  <span className="min-w-0">
                    <span className="block font-semibold leading-tight">{item.label}</span>
                    <span className={`block text-xs ${isActive ? 'text-[#0b1830]/85' : 'text-white/45'}`}>{item.desc}</span>
                  </span>
                ) : null}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {!mini ? (
        <p className="mt-auto truncate px-2 text-xs text-white/40">{session.user?.email}</p>
      ) : null}
    </div>
  );

  const HeaderActions = () => (
    <>
      <a
        href="/"
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-2 text-xs font-semibold text-[#14213d] transition hover:border-[#1fa5a3] hover:text-[#1fa5a3] sm:px-4 sm:text-sm"
      >
        <ExternalLink className="h-4 w-4" />
        <span className="hidden sm:inline">Ver la web</span>
      </a>
      <button
        type="button"
        onClick={handlePurge}
        disabled={purge !== 'idle'}
        title="Vacía la caché y recarga los datos"
        className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-2 text-xs font-semibold text-[#14213d] transition hover:border-[#1fa5a3] hover:text-[#1fa5a3] disabled:opacity-60 sm:px-4 sm:text-sm"
      >
        <RefreshCw className={`h-4 w-4 ${purge === 'working' ? 'animate-spin' : ''}`} />
        <span className="hidden sm:inline">
          {purge === 'working' ? 'Purgando…' : purge === 'done' ? 'Listo' : 'Purgar caché'}
        </span>
      </button>
      <button
        type="button"
        onClick={handleLogout}
        className="inline-flex items-center gap-2 rounded-full bg-[#14213d] px-3 py-2 text-xs font-semibold text-white transition hover:bg-[#0f1a31] sm:px-4 sm:text-sm"
      >
        <LogOut className="h-4 w-4" />
        <span className="hidden sm:inline">Cerrar sesión</span>
      </button>
    </>
  );

  return (
    <div className="flex min-h-screen bg-slate-100">
      {/* Desktop sidebar with a collapse handle */}
      <aside
        className={`sticky top-0 hidden h-screen flex-shrink-0 bg-[#14213d] transition-[width] duration-200 md:block ${
          collapsed ? 'w-20' : 'w-64'
        }`}
      >
        <Sidebar mini={collapsed} />
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          aria-label={collapsed ? 'Expandir menú' : 'Plegar menú'}
          title={collapsed ? 'Expandir menú' : 'Plegar menú'}
          className="absolute -right-3 top-20 flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-white text-[#14213d] shadow-md transition hover:text-[#1fa5a3]"
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
      </aside>

      {/* Mobile drawer */}
      {drawer ? (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDrawer(false)} />
          <aside className="absolute left-0 top-0 h-full w-72 overflow-y-auto bg-[#14213d]">
            <button
              type="button"
              aria-label="Cerrar menú"
              onClick={() => setDrawer(false)}
              className="absolute right-3 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white"
            >
              <X className="h-5 w-5" />
            </button>
            <Sidebar />
          </aside>
        </div>
      ) : null}

      {/* Content column */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur">
          <div className="flex items-center gap-3 px-4 py-3 md:px-6">
            <button
              type="button"
              aria-label="Abrir menú"
              onClick={() => setDrawer(true)}
              className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-slate-200 text-[#14213d] md:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Panel</p>
              <h1 className="truncate text-base font-bold text-[#14213d] sm:text-lg">
                {current?.label || 'Administración'}
              </h1>
            </div>
            <div className="flex flex-shrink-0 items-center gap-2">
              <HeaderActions />
            </div>
          </div>
        </header>

        <main className="min-w-0 flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
